// bluaIQ — Project List & Selector
// Shows all user projects with photo upload, grid/list toggle, create new

import { apiFetch } from '../utils/api'
import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus, MapPin, Calendar, Home, MoreVertical, Archive, CheckCircle,
  LayoutGrid, List, Camera, Image, X, Upload, Trash2, RotateCcw, FolderOpen,
  Users,
} from 'lucide-react'
import { DEMO_PROJECTS, isDemoProject, isDemoMode } from '../utils/demoData'
import { readLegacyMigratedItem, writeLegacyMigratedItem } from '../utils/legacyBrandStorage'

// ── TYPES ─────────────────────────────────────────

interface Project {
  id: string
  name: string
  address?: string
  city?: string
  state?: string
  type: string
  sqftTarget?: number
  status: string
  phase: number
  startDate?: string
  createdAt: string
  coverImage?: string // URL or base64
}

interface ProjectLimit {
  allowed: boolean
  current: number
  max: number
  extraPurchased: number
  extraProjectPrice: number
  canBuyExtra: boolean
}

// Phase names live in locales under projects.phases.p0..p4 — this array keeps
// the index → key mapping so cards can still look up by project.phase number.
const PHASE_KEYS = ['p0', 'p1', 'p2', 'p3', 'p4'] as const
const PHASE_COLORS: Record<number, string> = { 0: '#06B6D4', 1: '#3B82F6', 2: '#00BCD4', 3: '#14B8A6', 4: '#EC4899' }

// Demo cover images (placeholder — real ones come from user uploads)
const DEMO_COVERS: Record<string, string> = {
  'demo-1': 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&h=400&fit=crop',
  'demo-2': 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&h=400&fit=crop',
}

// ── COMPONENT ─────────────────────────────────────

export default function ProjectList() {
  const { getToken } = useAuth()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEs = i18n.language === 'es'
  // isEs still drives toLocaleDateString locale selection in GridCard/ListCard.

  const [showNewForm, setShowNewForm] = useState(false)
  const [showTrash, setShowTrash] = useState(false)
  // ConstructAI was the original product name; the view-mode and
  // cover-image keys are read with backwards-compatible legacy
  // migration so existing users keep their preferences. Writes go
  // to the new bluaiq_* keys (and mirror to the legacy keys during
  // the migration window).
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(
    () => (readLegacyMigratedItem('bluaiq_view', 'constructai_view') as 'grid' | 'list') || 'grid'
  )

  // Project cover images stored in localStorage (demo — in production: S3/storage)
  const [coverImages, setCoverImages] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(readLegacyMigratedItem('bluaiq_covers', 'constructai_covers') || '{}')
    } catch { return {} }
  })

  const setViewModeAndSave = (mode: 'grid' | 'list') => {
    setViewMode(mode)
    writeLegacyMigratedItem('bluaiq_view', 'constructai_view', mode)
  }

  const setCoverImage = (projectId: string, imageUrl: string) => {
    const updated = { ...coverImages, [projectId]: imageUrl }
    setCoverImages(updated)
    writeLegacyMigratedItem('bluaiq_covers', 'constructai_covers', JSON.stringify(updated))
  }

  const removeCoverImage = (projectId: string) => {
    const updated = { ...coverImages }
    delete updated[projectId]
    setCoverImages(updated)
    writeLegacyMigratedItem('bluaiq_covers', 'constructai_covers', JSON.stringify(updated))
  }

  const { data: apiProjects, isLoading, isError, refetch } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: async () => { const token = await getToken(); return apiFetch('/api/projects', { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })

  // Demo projects are for the EXPLICIT no-backend demo experience only
  // (marketing / prospect tour — isDemoMode() is false in production). Real
  // users must NEVER see DEMO_PROJECTS: when the API is loading or errored,
  // they get an honest loading skeleton or error state — never fake projects.
  const demoMode = isDemoMode()
  const projects = Array.isArray(apiProjects) ? apiProjects : (demoMode ? (DEMO_PROJECTS as Project[]) : [])
  const moveToTrash = useMutation({
    mutationFn: async (projectId: string) => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}`, {
        method: 'DELETE',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('Could not move project to trash')
      return projectId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['projects-trash'] })
    },
  })

  const activeProjects = projects?.filter(p => p.status === 'ACTIVE') || []
  const archivedProjects = projects?.filter(p => p.status !== 'ACTIVE') || []

  // Get cover for a project: user upload > demo > null
  const getCover = (id: string) => coverImages[id] || DEMO_COVERS[id] || null

  const handleMoveToTrash = (project: Project) => {
    const confirmText = isEs
      ? `¿Mover "${project.name}" a la papelera?`
      : `Move "${project.name}" to trash?`
    if (!window.confirm(confirmText)) return
    moveToTrash.mutate(project.id)
  }

  if (isLoading) return <ProjectListSkeleton />

  // Real users: an API failure shows an honest error + retry — NOT demo
  // projects and NOT a misleading "no projects" onboarding state. (Demo mode
  // keeps its fallback and never reaches here.)
  if (!demoMode && isError) {
    return (
      <div className="p-5 max-w-5xl mx-auto" data-testid="projects-error-state">
        <div className="bg-white border border-dashed border-[#FECACA] rounded-2xl p-10 text-center">
          <div className="w-14 h-14 rounded-full bg-[#FEF2F2] flex items-center justify-center mx-auto mb-4">
            <FolderOpen size={26} className="text-[#B91C1C]" />
          </div>
          <h3 className="text-[16px] font-[900] tracking-[-0.3px] text-[#1C1A17]">
            {isEs ? 'No se pudieron cargar los proyectos' : 'Couldn’t load projects'}
          </h3>
          <p className="text-[12px] font-[600] text-[#9E9890] mt-1.5 max-w-md mx-auto">
            {isEs
              ? 'No pudimos cargar tus proyectos. Verifica tu conexión e inténtalo de nuevo.'
              : 'We couldn’t load your projects. Check your connection and try again.'}
          </p>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-1.5 mt-5 bg-[#06539B] hover:bg-[#14457E] text-white text-[13px] font-[800] px-5 py-2.5 rounded-full transition-colors"
          >
            {isEs ? 'Reintentar' : 'Try again'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-5 max-w-5xl mx-auto space-y-4">
      {/* Demo back button */}
      <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-[11px] font-[700] text-[#9E9890] hover:text-[#1C1A17] transition-colors -mb-2">
        ← {t('projects.backToSite')}
      </button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-[900] tracking-[-0.5px] text-[#1C1A17]">
            {t('My Projects')}
          </h1>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {activeProjects.length} {t('active')} · {archivedProjects.length} {t('archived')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center bg-[#F2F0EB] rounded-lg p-0.5">
            <button
              onClick={() => setViewModeAndSave('grid')}
              className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-[#1C1A17]' : 'text-[#9E9890]'}`}
            >
              <LayoutGrid size={14} />
            </button>
            <button
              onClick={() => setViewModeAndSave('list')}
              className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-[#1C1A17]' : 'text-[#9E9890]'}`}
            >
              <List size={14} />
            </button>
          </div>
          <button
            onClick={() => navigate('/team')}
            aria-label={t('Team', { defaultValue: 'Equipo' })}
            data-testid="projects-team-link"
            className="flex items-center gap-1.5 min-h-[40px] text-[13px] font-[800] px-3 py-2 rounded-full border border-[#E8E4DC] text-[#1C1A17] hover:bg-[#F2F0EB] transition-colors"
          >
            <Users size={14} />
            <span className="hidden sm:inline">{t('Team', { defaultValue: 'Equipo' })}</span>
          </button>
          <button
            onClick={() => setShowNewForm(true)}
            className="flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#14457E] transition-colors"
          >
            <Plus size={14} />
            {t('New Project')}
          </button>
        </div>
      </div>

      {/* New project form */}
      {showNewForm && (
        <NewProjectForm
          onClose={() => setShowNewForm(false)}
          onCreated={(id) => { setShowNewForm(false); navigate(`/projects/${id}`) }}
        />
      )}

      {/* ── GRID VIEW ── */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {activeProjects.map(project => (
            <GridCard
              key={project.id}
              project={project}
              isEs={isEs}
              coverUrl={getCover(project.id)}
              onSetCover={(url) => setCoverImage(project.id, url)}
              onRemoveCover={() => removeCoverImage(project.id)}
              onDelete={() => handleMoveToTrash(project)}
              isDeleting={moveToTrash.isPending && moveToTrash.variables === project.id}
              onClick={() => navigate(`/projects/${project.id}`)}
            />
          ))}
        </div>
      )}

      {/* ── LIST VIEW ── */}
      {viewMode === 'list' && (
        <div className="space-y-2">
          {activeProjects.map(project => (
            <ListCard
              key={project.id}
              project={project}
              isEs={isEs}
              coverUrl={getCover(project.id)}
              onSetCover={(url) => setCoverImage(project.id, url)}
              onRemoveCover={() => removeCoverImage(project.id)}
              onDelete={() => handleMoveToTrash(project)}
              isDeleting={moveToTrash.isPending && moveToTrash.variables === project.id}
              onClick={() => navigate(`/projects/${project.id}`)}
            />
          ))}
        </div>
      )}

      {/* Onboarding wizard for new users */}
      {activeProjects.length === 0 && !showNewForm && (
        <OnboardingWizard
          getToken={getToken}
          onCreated={(id) => { queryClient.invalidateQueries({ queryKey: ['projects'] }); navigate(`/projects/${id}`) }}
        />
      )}

      {/* Trash button */}
      <div className="flex justify-center">
        <button
          onClick={() => setShowTrash(!showTrash)}
          className="flex items-center gap-1.5 text-[12px] font-[700] text-[#9E9890] hover:text-[#1C1A17] transition-colors"
        >
          <Trash2 size={12} /> {t('projects.trash')}
        </button>
      </div>

      {showTrash && (
        <TrashPanel getToken={getToken} onRestore={() => queryClient.invalidateQueries({ queryKey: ['projects'] })} />
      )}

      {/* Archived */}
      {archivedProjects.length > 0 && (
        <div>
          <h3 className="text-[12px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-2">
            {t('Archived')}
          </h3>
          <div className="space-y-2">
            {archivedProjects.map(p => (
              <div
                key={p.id}
                onClick={() => navigate(`/projects/${p.id}`)}
                className="flex items-center gap-3 p-3 bg-white border border-[#ECEAE4] rounded-xl cursor-pointer hover:border-[#9E9890] transition-colors opacity-60"
              >
                {getCover(p.id) && (
                  <img src={getCover(p.id)!} alt="" className="w-[40px] h-[40px] rounded-lg object-cover" />
                )}
                <div className="w-[8px] h-[8px] rounded-full bg-[#9E9890]" />
                <span className="text-[12px] font-[700] flex-1">{p.name}</span>
                <span className="text-[12px] font-[700] text-[#9E9890]">
                  {p.status === 'COMPLETED' ? t('Completed') : t('Archived')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── GRID CARD (foto como fondo) ───────────────────

function GridCard({ project, isEs, coverUrl, onSetCover, onRemoveCover, onDelete, isDeleting, onClick }: {
  project: Project; isEs: boolean; coverUrl: string | null;
  onSetCover: (url: string) => void; onRemoveCover: () => void; onDelete: () => void; isDeleting: boolean; onClick: () => void
}) {
  const { t } = useTranslation()
  const phaseKey = PHASE_KEYS[project.phase] ?? PHASE_KEYS[0]
  const typeLabel = t(`projects.types.${project.type}`, project.type)
  const phaseColor = PHASE_COLORS[project.phase] || '#94A3B8'
  const [showUpload, setShowUpload] = useState(false)

  return (
    <div className="relative group rounded-2xl overflow-hidden bg-white border border-[#ECEAE4] hover:shadow-lg hover:shadow-black/5 transition-all duration-300 cursor-pointer"
      onClick={onClick}>

      {/* Cover image area */}
      <div className="relative h-[180px] bg-gradient-to-br from-[#F2F0EB] to-[#ECEAE4] overflow-hidden">
        {coverUrl ? (
          <img src={coverUrl} alt={project.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Home size={40} className="text-[#ECEAE4]" />
          </div>
        )}

        {/* Gradient overlay at bottom */}
        <div className="absolute inset-x-0 bottom-0 h-[80px] bg-gradient-to-t from-black/60 to-transparent" />

        {/* Phase badge */}
        <span className="absolute top-3 right-3 text-[8px] font-[800] px-2.5 py-1 rounded-full text-white backdrop-blur-sm"
          style={{ background: `${phaseColor}cc` }}>
          {t(`projects.phases.${phaseKey}`)}
        </span>

        {/* Demo badge — only renders for projects whose id matches
            `isDemoProject` (i.e. the FE-only DEMO_PROJECT_IDS). Under
            normal operation no real project will trigger this; the
            badge exists for the offline-fallback path that swaps
            `apiProjects` for `DEMO_PROJECTS`, so a prospect or a
            user on a degraded connection sees an honest "this is
            demo data, not your saved project" cue instead of demo
            content masquerading as real. */}
        {isDemoProject(project.id) && (
          <span
            data-testid="project-card-demo-badge"
            className="absolute top-10 right-3 text-[8px] font-[800] px-2.5 py-1 rounded-full bg-white/90 text-[#1C1A17] border border-[#ECEAE4] backdrop-blur-sm"
          >
            {t('projectList.demoBadge')}
          </span>
        )}

        {/* Photo upload button (on hover) */}
        <button
          onClick={(e) => { e.stopPropagation(); setShowUpload(true) }}
          className="absolute top-3 left-3 w-[28px] h-[28px] rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
        >
          <Camera size={13} className="text-[#1C1A17]" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation()
            const confirmed = window.confirm(
              isEs
                ? `¿Mover "${project.name}" a la papelera? Podrás restaurarlo desde la papelera durante 30 días.`
                : `Move "${project.name}" to trash? You can restore it from trash for 30 days.`
            )
            if (confirmed) onDelete()
          }}
          disabled={isDeleting}
          title={t('projects.trash')}
          aria-label={isEs ? 'Eliminar proyecto' : 'Delete project'}
          className="absolute top-3 left-11 w-[28px] h-[28px] rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center opacity-60 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-white disabled:opacity-100"
        >
          <Trash2 size={13} className={isDeleting ? 'text-red-300' : 'text-red-500'} />
        </button>

        {/* Project name on image */}
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="text-[15px] font-[800] text-white truncate drop-shadow-md">{project.name}</h3>
          {project.address && (
            <div className="flex items-center gap-1 mt-0.5">
              <MapPin size={9} className="text-white/70" />
              <span className="text-[10px] font-[600] text-white/80 truncate">
                {project.address}{project.city ? `, ${project.city}` : ''}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Bottom info */}
      <div className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[12px] font-[700] text-[#9E9890]">{typeLabel}</span>
          {project.sqftTarget && (
            <span className="text-[9px] font-[600] text-[#9E9890]">· {project.sqftTarget.toLocaleString()} sqft</span>
          )}
        </div>

        {/* Phase progress bar */}
        <div className="h-[3px] bg-[#ECEAE4] rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${(project.phase / 4) * 100}%`, background: phaseColor }} />
        </div>

        {project.startDate && (
          <div className="flex items-center gap-1 mt-2">
            <Calendar size={10} className="text-[#9E9890]" />
            <span className="text-[9px] font-[600] text-[#9E9890]">
              {new Date(project.startDate).toLocaleDateString(isEs ? 'es-MX' : 'en-US', { month: 'short', year: 'numeric' })}
            </span>
          </div>
        )}
      </div>

      {/* Upload overlay */}
      {showUpload && (
        <PhotoUploadOverlay
          onUpload={(url) => { onSetCover(url); setShowUpload(false) }}
          onRemove={() => { onRemoveCover(); setShowUpload(false) }}
          onClose={() => setShowUpload(false)}
          hasCover={!!coverUrl}
        />
      )}
    </div>
  )
}

// ── LIST CARD (thumbnail + info al lado) ──────────

function ListCard({ project, isEs, coverUrl, onSetCover, onRemoveCover, onDelete, isDeleting, onClick }: {
  project: Project; isEs: boolean; coverUrl: string | null;
  onSetCover: (url: string) => void; onRemoveCover: () => void; onDelete: () => void; isDeleting: boolean; onClick: () => void
}) {
  const { t } = useTranslation()
  const phaseKey = PHASE_KEYS[project.phase] ?? PHASE_KEYS[0]
  const typeLabel = t(`projects.types.${project.type}`, project.type)
  const phaseColor = PHASE_COLORS[project.phase] || '#94A3B8'
  const [showUpload, setShowUpload] = useState(false)

  return (
    <div className="relative flex items-center gap-4 bg-white border border-[#ECEAE4] rounded-xl p-3 cursor-pointer hover:border-[#06539B] hover:shadow-sm transition-all group"
      onClick={onClick}>

      {/* Thumbnail */}
      <div className="relative w-[80px] h-[80px] rounded-xl overflow-hidden flex-shrink-0 bg-[#F2F0EB]">
        {coverUrl ? (
          <img src={coverUrl} alt={project.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Home size={24} className="text-[#ECEAE4]" />
          </div>
        )}
        {/* Photo upload on hover */}
        <button
          onClick={(e) => { e.stopPropagation(); setShowUpload(true) }}
          className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Camera size={16} className="text-white" />
        </button>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-[14px] font-[800] text-[#1C1A17] truncate">{project.name}</h3>
            {project.address && (
              <div className="flex items-center gap-1 mt-0.5">
                <MapPin size={10} className="text-[#9E9890] flex-shrink-0" />
                <span className="text-[10px] font-[600] text-[#9E9890] truncate">
                  {project.address}{project.city ? `, ${project.city}` : ''}
                </span>
              </div>
            )}
          </div>
          <span className="text-[9px] font-[800] px-2.5 py-1 rounded-full text-white flex-shrink-0 ml-2"
            style={{ background: phaseColor }}>
            {t(`projects.phases.${phaseKey}`)}
          </span>
        </div>

        <div className="flex items-center gap-2 mt-2">
          <span className="text-[12px] font-[700] text-[#9E9890]">{typeLabel}</span>
          {project.sqftTarget && (
            <span className="text-[9px] font-[600] text-[#9E9890]">· {project.sqftTarget.toLocaleString()} sqft</span>
          )}
          {project.startDate && (
            <span className="text-[9px] font-[600] text-[#9E9890]">
              · {new Date(project.startDate).toLocaleDateString(isEs ? 'es-MX' : 'en-US', { month: 'short', year: 'numeric' })}
            </span>
          )}
          {/* Demo badge — same isDemoProject gate as GridCard. */}
          {isDemoProject(project.id) && (
            <span
              data-testid="project-list-demo-badge"
              className="text-[9px] font-[800] px-2 py-0.5 rounded-full bg-[#F2F0EB] text-[#1C1A17] border border-[#ECEAE4]"
            >
              {t('projectList.demoBadge')}
            </span>
          )}
        </div>

        {/* Phase progress bar */}
        <div className="h-[3px] bg-[#ECEAE4] rounded-full overflow-hidden mt-2.5">
          <div className="h-full rounded-full transition-all" style={{ width: `${(project.phase / 4) * 100}%`, background: phaseColor }} />
        </div>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete() }}
        disabled={isDeleting}
        title={t('projects.trash')}
        className="p-2 rounded-full hover:bg-red-50 transition-colors disabled:opacity-60"
      >
        <Trash2 size={14} className={isDeleting ? 'text-red-300' : 'text-red-500'} />
      </button>

      {/* Upload overlay */}
      {showUpload && (
        <PhotoUploadOverlay
          onUpload={(url) => { onSetCover(url); setShowUpload(false) }}
          onRemove={() => { onRemoveCover(); setShowUpload(false) }}
          onClose={() => setShowUpload(false)}
          hasCover={!!coverUrl}
        />
      )}
    </div>
  )
}

// ── PHOTO UPLOAD OVERLAY ──────────────────────────

function PhotoUploadOverlay({ onUpload, onRemove, onClose, hasCover }: {
  onUpload: (url: string) => void; onRemove: () => void; onClose: () => void;
  hasCover: boolean
}) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Convert to base64 for demo (in production: upload to S3)
    const reader = new FileReader()
    reader.onload = (ev) => {
      const base64 = ev.target?.result as string
      onUpload(base64)
    }
    reader.readAsDataURL(file)
  }

  return (
    <div className="absolute inset-0 z-10 bg-black/50 backdrop-blur-sm rounded-2xl flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}>
      <div className="bg-white rounded-xl p-4 shadow-xl max-w-[200px] w-full mx-4 space-y-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[11px] font-[800] text-[#1C1A17]">
            {t('projects.photo.title')}
          </span>
          <button onClick={onClose}><X size={14} className="text-[#9E9890]" /></button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
          className="hidden"
        />

        <button
          onClick={() => fileInputRef.current?.click()}
          className="w-full flex items-center gap-2 justify-center bg-[#06539B] text-white text-[13px] font-[800] py-2.5 rounded-full hover:bg-[#14457E]"
        >
          <Upload size={13} /> {t('projects.photo.upload')}
        </button>

        {hasCover && (
          <button
            onClick={onRemove}
            className="w-full flex items-center gap-2 justify-center border border-red-200 text-red-500 text-[13px] font-[800] py-2 rounded-full hover:bg-red-50"
          >
            <X size={12} /> {t('projects.photo.remove')}
          </button>
        )}
      </div>
    </div>
  )
}

// ── NEW PROJECT FORM ──────────────────────────────

function NewProjectForm({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { getToken } = useAuth()
  const { t } = useTranslation()
  const [form, setForm] = useState({
    name: '', address: '', city: '', state: 'TX', zipCode: '', type: 'SPEC', sqftTarget: ''
  })

  const mutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      const res = await apiFetch('/api/projects', {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          sqftTarget: form.sqftTarget ? Number(form.sqftTarget) : undefined,
          startDate: new Date().toISOString()
        })
      })
      if (!res.ok) throw new Error('Could not create project. Please check your connection.')
      return res.json()
    },
    onSuccess: (data) => onCreated(data.id),
    onError: () => {}
  })

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
      <h3 className="text-[13px] font-[800] text-[#1C1A17]">{t('New Project')}</h3>

      <input
        placeholder={t('Project name')}
        value={form.name}
        onChange={e => setForm({ ...form, name: e.target.value })}
        className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
      />

      {/* ── Job site address ── */}
      <div className="space-y-2 pt-1">
        <div>
          <h4 className="text-[11px] font-[800] text-[#1C1A17] uppercase tracking-[0.5px]">
            {t('projects.address.title')}
          </h4>
          <p className="text-[10px] font-[600] text-[#9E9890] mt-0.5">
            {t('projects.address.hint')}
          </p>
        </div>

        <input
          placeholder={t('projects.address.streetPlaceholder')}
          value={form.address}
          onChange={e => setForm({ ...form, address: e.target.value })}
          className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
        />

        <div className="grid grid-cols-3 gap-2">
          <input
            placeholder={t('City')}
            value={form.city}
            onChange={e => setForm({ ...form, city: e.target.value })}
            className="col-span-2 border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
          />
          <input
            value={form.state}
            onChange={e => setForm({ ...form, state: e.target.value.toUpperCase() })}
            className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
            maxLength={2}
            placeholder="TX"
          />
        </div>

        <input
          placeholder={t('projects.address.zipPlaceholder')}
          value={form.zipCode}
          onChange={e => setForm({ ...form, zipCode: e.target.value })}
          className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
          maxLength={10}
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <select
          value={form.type}
          onChange={e => setForm({ ...form, type: e.target.value })}
          className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
        >
          <option value="SPEC">{t('Spec Home')}</option>
          <option value="CUSTOM">{t('Custom Home')}</option>
          <option value="REMODEL">{t('Remodel')}</option>
        </select>
        <input
          placeholder={t('Sqft')}
          type="number"
          value={form.sqftTarget}
          onChange={e => setForm({ ...form, sqftTarget: e.target.value })}
          className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
        />
      </div>

      {mutation.isError && (
        <p className="text-[11px] text-red-600 font-[600]">Could not create project. Please check your connection.</p>
      )}

      <div className="flex gap-2 pt-1">
        <button
          onClick={onClose}
          className="flex-1 border border-[#ECEAE4] rounded-full py-2 text-[11px] font-[800] text-[#9E9890] hover:bg-[#F2F0EB] transition-colors"
        >
          {t('Cancel')}
        </button>
        <button
          onClick={() => mutation.mutate()}
          disabled={!form.name || mutation.isPending}
          className="flex-1 bg-[#06539B] text-white rounded-full py-2 text-[11px] font-[800] hover:bg-[#14457E] transition-colors disabled:opacity-40"
        >
          {mutation.isPending ? '...' : t('Create')}
        </button>
      </div>
    </div>
  )
}

// ── ONBOARDING WIZARD ────────────────────────────

function OnboardingWizard({ getToken, onCreated }: { getToken: () => Promise<string | null>; onCreated: (id: string) => void }) {
  const { t } = useTranslation()
  const [form, setForm] = useState({ name: '', address: '', city: '', state: 'TX', zipCode: '', type: 'SPEC', sqftTarget: '' })
  const [creating, setCreating] = useState(false)

  const inputClass = "w-full border border-[#ECEAE4] rounded-lg px-3 py-2.5 text-[13px] font-[600] focus:outline-none focus:border-[#06539B] focus:ring-1 focus:ring-[#06539B]/20"

  const handleCreate = async () => {
    setCreating(true)
    try {
      const token = await getToken()
      const res = await apiFetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ ...form, sqftTarget: form.sqftTarget ? Number(form.sqftTarget) : undefined, startDate: new Date().toISOString() }),
      })
      if (!res.ok) throw new Error('Failed')
      const data = await res.json()
      onCreated(data.id)
    } catch { /* silent */ }
    setCreating(false)
  }

  return (
    <div className="max-w-lg mx-auto py-8 space-y-6">
      <div className="bg-white border border-[#ECEAE4] rounded-2xl p-6 space-y-4">
        <div className="text-center mb-2">
          <Home size={32} className="mx-auto text-[#F5A623] mb-2" />
          <h2 className="text-[18px] font-[900] text-[#1C1A17]">{t('projects.wizard.step1Title')}</h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-1">{t('projects.wizard.step1Subtitle')}</p>
        </div>
        <input placeholder={t('projects.wizard.step1NamePlaceholder')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputClass} />

        {/* ── Job site address ── */}
        <div className="space-y-2">
          <div>
            <h4 className="text-[11px] font-[800] text-[#1C1A17] uppercase tracking-[0.5px]">
              {t('projects.address.title')}
            </h4>
            <p className="text-[10px] font-[600] text-[#9E9890] mt-0.5">
              {t('projects.address.hint')}
            </p>
          </div>
          <input placeholder={t('projects.address.streetPlaceholder')} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className={inputClass} />
          <div className="grid grid-cols-3 gap-2">
            <input placeholder={t('City')} value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className={`${inputClass} col-span-2`} />
            <input value={form.state} onChange={e => setForm({ ...form, state: e.target.value.toUpperCase() })} className={inputClass} maxLength={2} placeholder="TX" />
          </div>
          <input placeholder={t('projects.address.zipPlaceholder')} value={form.zipCode} onChange={e => setForm({ ...form, zipCode: e.target.value })} className={inputClass} maxLength={10} />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className={inputClass}>
            <option value="SPEC">{t('projects.types.SPEC')}</option>
            <option value="CUSTOM">{t('projects.types.CUSTOM')}</option>
            <option value="REMODEL">{t('projects.types.REMODEL')}</option>
          </select>
          <input placeholder={t('Sqft')} type="number" value={form.sqftTarget} onChange={e => setForm({ ...form, sqftTarget: e.target.value })} className={inputClass} />
        </div>
        <p className="text-[11px] text-[#9E9890] mt-3 text-center">
          {t('onboarding.nextStepHint')}
        </p>
        <button onClick={handleCreate} disabled={!form.name || creating}
          className="w-full bg-[#F5A623] hover:bg-[#E6961A] text-white rounded-full py-3 text-[13px] font-[800] disabled:opacity-40 transition-colors">
          {creating ? '...' : t('onboarding.createProject')}
        </button>
      </div>
    </div>
  )
}

// ── TRASH PANEL ──────────────────────────────────

function TrashPanel({ getToken, onRestore }: { getToken: () => Promise<string | null>; onRestore: () => void }) {
  const { t, i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const { data: trashed, refetch } = useQuery<any[]>({
    queryKey: ['projects-trash'],
    queryFn: async () => { const token = await getToken(); return apiFetch('/api/projects/trash', { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => r.ok ? r.json() : []) },
  })
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  const handleRestore = async (id: string) => {
    const token = await getToken()
    await apiFetch(`/api/projects/${id}/restore`, { method: 'POST', headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } })
    refetch()
    onRestore()
  }

  const handlePermanentDelete = async (id: string) => {
    const token = await getToken()
    const res = await apiFetch(`/api/projects/${id}/permanent`, {
      method: 'DELETE',
      headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
    })
    if (!res.ok) return
    setConfirmDelete(null)
    refetch()
    onRestore()
  }

  if (!trashed || trashed.length === 0) {
    return (
      <div className="bg-[#FAFAF8] border border-[#ECEAE4] rounded-xl p-6 text-center">
        <Trash2 size={24} className="mx-auto text-[#ECEAE4] mb-2" />
        <p className="text-[11px] font-[700] text-[#9E9890]">{t('projects.trashEmpty')}</p>
      </div>
    )
  }

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
      <div className="px-4 py-2.5 border-b border-[#ECEAE4] bg-[#FAFAF8]">
        <span className="text-[11px] font-[800] text-[#9E9890]">
          <Trash2 size={12} className="inline mr-1.5" />
          {t('projects.trash')} ({trashed.length})
        </span>
      </div>
      <div className="divide-y divide-[#F2F0EB]">
        {trashed.map(project => (
          <div key={project.id} className="flex items-center gap-3 px-4 py-3">
            <div className="flex-1 min-w-0">
              <span className="text-[12px] font-[700] text-[#1C1A17]">{project.name}</span>
              {project.address && <span className="text-[10px] font-[600] text-[#9E9890] ml-2">{project.address}</span>}
              <div className="text-[9px] font-[600] text-[#9E9890]">
                {t('Deleted')} {new Date(project.deletedAt).toLocaleDateString(isEs ? 'es-MX' : 'en-US')}
              </div>
            </div>
            <button
              onClick={() => handleRestore(project.id)}
              className="flex items-center gap-1 px-3 py-1.5 border border-[#ECEAE4] rounded-full text-[10px] font-[800] text-[#1C1A17] hover:bg-[#F2F0EB] transition-colors"
            >
              <RotateCcw size={11} /> {t('Restore')}
            </button>
            {confirmDelete === project.id ? (
              <button
                onClick={() => handlePermanentDelete(project.id)}
                className="px-3 py-1.5 bg-red-500 text-white rounded-full text-[10px] font-[800]"
              >
                {t('Confirm')}
              </button>
            ) : (
              <button
                onClick={() => setConfirmDelete(project.id)}
                className="p-1.5 rounded-full hover:bg-red-50 transition-colors"
                title={t('Delete permanently')}
              >
                <Trash2 size={12} className="text-[#9E9890] hover:text-red-500" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── SKELETON ──────────────────────────────────────

function ProjectListSkeleton() {
  return (
    <div className="p-5 max-w-5xl mx-auto space-y-4 animate-pulse">
      <div className="h-8 w-48 bg-[#ECEAE4] rounded-lg" />
      <div className="grid grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-[#ECEAE4] rounded-2xl h-[240px]" />
        ))}
      </div>
    </div>
  )
}
